import React, {CSSProperties, useCallback, useEffect, useId, useRef, useState} from 'react';
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import DataCell from "./components/templates/table-components/datacell";
import {ITableDataEntry, ITableDataEntryValue, ProductTemplate} from "./utils/interfaces";
import {AttributeHeader} from "./components/templates/table-components/header/AttributeHeader";

import {checkForNumberType, checkForUnwantedType, getHighlightColor, getValueColors} from "./utils/utils";
import {Paginator} from "primereact/paginator";
import {ReportOptions} from "./ProductTemplateTable";

interface ProductTemplateNonTransposedTableProps extends ProductTemplate.IHorizontalCommonConfig {
    configData: any;
    templateData: Array<any>;

    selectedEntryIndex: number | undefined;
    selectedEntryId: string | undefined;
    selectedEntryBody: ITableDataEntry | undefined;

    itemsPerPage?: number

    reportOptions?: ReportOptions;

    EditRowDataCell(val: any, index: number): JSX.Element
    editedDataHandler(indexEntry: number, refName: string, newValue: any, newCell?: ITableDataEntryValue): void;
    onClickSign(entryIndex: number, referenceName: string): void;
    handleTableKeyDown(key: string): void
}

const ProductTemplateTableDefault = ({templateData,
                                         configData,
                                         selectedEntryBody,
                                         selectedEntryId,
                                         selectedEntryIndex,
                                         itemsPerPage,
                                         editedDataHandler,
                                         onClickSign,
                                         handleTableKeyDown,
                                         EditRowDataCell,
                                         reportOptions,
                                         editColumnLocation = "BACK",
                                     }: ProductTemplateNonTransposedTableProps) => {
    const tableId = useId().replace(/[^a-zA-Z0-9]/g, '');
    const divRef = useRef<HTMLDivElement>(null);

    const [selected, setSelected] = useState(null);

    const [page, setPage] = useState(0);
    const [rows, setRows] = useState(itemsPerPage)

    const rowsPerPageOptions = itemsPerPage ? [itemsPerPage, 10, 20, 30]
        .sort((a, b) => a - b)
        .filter(function (x, i, a) {
            return a.indexOf(x) == i;
        }) : [];

    const handleWheel = useCallback((event: any) => {
        if (event.altKey) {
            event.preventDefault();
            const div: HTMLDivElement = event.currentTarget as HTMLDivElement

            div.scrollLeft += event.deltaY * 0.45
        }
    }, []);

    useEffect(() => {
        if (reportOptions && document.getElementsByClassName("print-mode-enabled").length > 0) {
            const table = document.querySelector(`#${tableId}`);
            if (table) {
                const allChildren = table.querySelectorAll('*')

                if (reportOptions.fontSize) {
                    for (let i = 0; i < allChildren.length; i++) {
                        allChildren[i].style.fontSize = reportOptions?.fontSize;
                    }
                }
            }
        }
    }, [])

    useEffect(() => {
        if (divRef.current) {
            divRef.current.addEventListener('wheel', handleWheel);
        }

        return () => {
            if (divRef.current) {
                divRef.current.removeEventListener('wheel', handleWheel);
            }
        };
    }, [divRef, handleWheel]);

    const dataCell = (val: any, index: number, configVal: any, configIndex: number) => {
        const dataValue = selectedEntryBody && selectedEntryIndex === index ? selectedEntryBody : val;
        const obj = dataValue.entries[configVal.referenceName];

        let color, bgColor;
        if (obj && obj.type && checkForNumberType(obj.type)) {
            [color, bgColor] = getValueColors(obj.value, {
                highlightRanges: configVal.bgHighlightRanges,
                defaultHighlightColor: configVal.highlightColor
            })
        } else {
            [color, bgColor] = getHighlightColor(configVal.highlightColor)
        }

        return <div style={styleHandler(color, bgColor, obj && obj.type)}>
            <DataCell key={val.id}
                      headerConfig={configVal}
                      dataValue={dataValue}
                      indexHeader={configIndex}
                      indexEntry={index}
                      changeHandler={editedDataHandler}
                      isInEditMode={selectedEntryId === val.id}
                      onClickSign={onClickSign}
            /></div>
    }

    const styleHandler = (color: string | undefined, bgColor: string | undefined, type: string | undefined): CSSProperties | undefined => {
        if (checkForUnwantedType(type)) {
            if (color && bgColor) {
                return {
                    overflowX: "auto",
                    color: color,
                    backgroundColor: bgColor
                }
            }
            if (bgColor) {
                return {
                    overflowX: "auto",
                    backgroundColor: bgColor
                }
            }

            if (color) {
                return {
                    overflowX: "auto",
                    color: color
                }
            }
        }

        return {
            overflowX: "auto",
        }
    }

    const EditColumn = (frozen: boolean = false) => {
        return <Column className={"non-printable"} frozen={frozen} body={(rowData, options) => EditRowDataCell(rowData, options.rowIndex)} style={{width: "0"}}/>
    }

    const dynamicColumns = configData.data.attributes.map((col: any, i: number) => {
        return <Column key={col.referenceName}
                       field={col.referenceName}
                       className={"change-padding"}
                       header={<AttributeHeader key={col.referenceName}
                                                attribute={col}
                                                noTh={true}/>}
                       body={(rowData: any, options) => dataCell(rowData, options.rowIndex, col, i)}
        />
    });

    const allColumns = () => {
        const columns: JSX.Element[] = [...dynamicColumns];

        if (editColumnLocation === "FRONT") {
            columns.unshift(EditColumn(true));
        } else if (editColumnLocation === "BACK") {
            columns.push(EditColumn(false));
        } else {
            console.error("Invalid edit column location")
        }

        return columns;
    }


    return (
        <>
            <div className={"default-table"}
                 id={tableId}
                 style={{overflowX: "auto"}}
                 onKeyDown={(e) => handleTableKeyDown(e.key)}
                 ref={divRef}
            >
                <DataTable
                    value={rows ? [...templateData.slice(page, page + rows)] : templateData}
                    size={"small"}
                    showGridlines
                    selectionMode="single"
                    selection={selected}
                    onSelectionChange={e => setSelected(e.value)}
                >
                    {allColumns()}

                </DataTable>
            </div>
            {itemsPerPage &&
                <Paginator totalRecords={templateData.length}
                           rows={rows}
                           first={page}
                           onPageChange={(e) => {
                               setPage(e.first)
                               setRows(e.rows)
                           }}
                           template={"CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"}
                           currentPageReportTemplate={"Showing {first} to {last} of {totalRecords}"}
                           rowsPerPageOptions={rowsPerPageOptions}
                />
            }
        </>
    );
};

export default ProductTemplateTableDefault;